import React from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import StudentResumeView from "../components/StudentResumeView";
import { hirerResumeTheme } from "./hirerResumeTheme";
import {
  mapStudentToResumeData,
  sanitizeResumeFileName,
  type ResumeData,
  type ResumeStudentSource,
} from "./mapStudentToResumeData";

const RESUME_RENDER_WIDTH_PX = 794;

// Emotion keys may only contain lowercase letters and hyphens (no digits).
const pdfEmotionCache = createCache({ key: "resumepdf", prepend: true });

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isMostlyWhiteRow(row: Uint8ClampedArray, width: number): boolean {
  let white = 0;
  for (let i = 0; i < width; i++) {
    const o = i * 4;
    if (row[o] >= 248 && row[o + 1] >= 248 && row[o + 2] >= 248) white += 1;
  }
  return white / width >= 0.985;
}

function rowWhiteness(row: Uint8ClampedArray, width: number): number {
  let white = 0;
  for (let i = 0; i < width; i++) {
    const o = i * 4;
    if (row[o] >= 248 && row[o + 1] >= 248 && row[o + 2] >= 248) white += 1;
  }
  return white / width;
}

function hasWhiteBand(
  ctx: CanvasRenderingContext2D,
  y: number,
  width: number,
  height: number,
  band: number
): boolean {
  const top = Math.max(0, y - band);
  const bottom = Math.min(height - 1, y + band);
  for (let rowY = top; rowY <= bottom; rowY++) {
    const row = ctx.getImageData(0, rowY, width, 1).data;
    if (!isMostlyWhiteRow(row, width)) return false;
  }
  return true;
}

/**
 * Never bisect glyphs: break only inside a white gap between lines.
 * Prefers cutting above the ideal end (extra bottom space) over mid-line cuts.
 */
function findSafePageBreakY(
  source: HTMLCanvasElement,
  startY: number,
  idealEndY: number,
  lookbackPx: number
): number {
  if (idealEndY >= source.height) return source.height;
  const ctx = source.getContext("2d", { willReadFrequently: true });
  if (!ctx) return idealEndY;

  const width = source.width;
  const height = source.height;
  const minContentHeight = Math.max(80, Math.floor((idealEndY - startY) * 0.45));
  const minY = startY + minContentHeight;
  const searchFrom = Math.max(minY, idealEndY - lookbackPx);
  const band = 2; // require ~5px white gap (with scale:2 ≈ clear line spacing)

  // Ideal cut already in a gap — use it
  if (hasWhiteBand(ctx, Math.min(idealEndY, height - 1), width, height, band)) {
    return Math.min(idealEndY, height);
  }

  // Walk upward through nearby gaps (leave blank space rather than split letters)
  for (let y = idealEndY - 1; y >= searchFrom; y--) {
    if (hasWhiteBand(ctx, y, width, height, band)) return y;
  }

  // Expand search to entire allowed region if needed
  for (let y = searchFrom - 1; y >= minY; y--) {
    if (hasWhiteBand(ctx, y, width, height, band)) return y;
  }

  // Fallback: whitest row in range (avoid mid-ink even if no perfect gap)
  let bestY = idealEndY;
  let bestScore = -1;
  for (let y = idealEndY; y >= minY; y--) {
    const score = rowWhiteness(ctx.getImageData(0, y, width, 1).data, width);
    if (score > bestScore) {
      bestScore = score;
      bestY = y;
    }
  }
  return Math.max(minY, bestY);
}


/** Convert remote profile pic to a data URL so html2canvas can embed it without CORS taint. */
async function profilePicToDataUrl(url: string | undefined): Promise<string | undefined> {
  if (!url?.trim()) return undefined;
  const src = url.trim();
  if (src.startsWith("data:")) return src;

  const blobToDataUrl = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("read failed"));
      reader.readAsDataURL(blob);
    });

  // 1) fetch → blob → data URL
  try {
    const res = await fetch(src, { mode: "cors", credentials: "omit", cache: "no-cache" });
    if (res.ok) {
      const dataUrl = await blobToDataUrl(await res.blob());
      if (dataUrl.startsWith("data:image")) return dataUrl;
    }
  } catch {
    // fall through
  }

  // 2) Image + canvas (CDN must send Access-Control-Allow-Origin)
  try {
    const dataUrl = await new Promise<string | undefined>((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.referrerPolicy = "no-referrer";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth || 140;
          c.height = img.naturalHeight || 140;
          const ctx = c.getContext("2d");
          if (!ctx) {
            resolve(undefined);
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(c.toDataURL("image/jpeg", 0.92));
        } catch {
          resolve(undefined);
        }
      };
      img.onerror = () => resolve(undefined);
      const separator = src.includes("?") ? "&" : "?";
      img.src = `${src}${separator}pdfembed=1`;
    });
    if (dataUrl?.startsWith("data:image")) return dataUrl;
  } catch {
    // fall through
  }

  return undefined;
}

async function waitForImages(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          setTimeout(done, 3000);
        })
    )
  );
}

async function resumeDataToPdfBlob(data: ResumeData): Promise<Blob> {
  const embeddedPic = await profilePicToDataUrl(data.profilePicUrl);
  const pdfData: ResumeData = { ...data, profilePicUrl: embeddedPic };

  const host = document.createElement("div");
  Object.assign(host.style, {
    position: "fixed",
    left: "-10000px",
    top: "0px",
    width: `${RESUME_RENDER_WIDTH_PX}px`,
    background: "#ffffff",
    opacity: "1",
    visibility: "visible",
    pointerEvents: "none",
    overflow: "visible",
  });
  document.body.appendChild(host);

  const root = createRoot(host);
  try {
    root.render(
      <CacheProvider value={pdfEmotionCache}>
        <ThemeProvider theme={hirerResumeTheme}>
          <div id="resume-pdf-root" style={{ width: RESUME_RENDER_WIDTH_PX, background: "#ffffff" }}>
            <StudentResumeView data={pdfData} />
          </div>
        </ThemeProvider>
      </CacheProvider>
    );
    await waitForPaint();
    await sleep(250);
    const target = host.querySelector("#resume-pdf-root") as HTMLElement | null;
    if (target) await waitForImages(target);

    if (!target || target.offsetHeight < 20) {
      throw new Error("Resume failed to render for PDF");
    }

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,
      width: RESUME_RENDER_WIDTH_PX,
      windowWidth: RESUME_RENDER_WIDTH_PX,
      foreignObjectRendering: false,
      onclone: (_doc: Document, cloned: HTMLElement) => {
        cloned.style.opacity = "1";
        cloned.style.visibility = "visible";
        cloned.style.transform = "none";
      },
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Failed to render resume for PDF (empty canvas)");
    }

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    // Extra margins; avoid clipping glyphs near page edges
    const pagePadMm = (50 * 25.4) / 96;
    const usableHeightMm = pageHeight - pagePadMm * 2;
    const imgWidthMm = pageWidth;
    const pxPerMm = canvas.width / imgWidthMm;
    const usableHeightPx = Math.max(1, Math.floor(usableHeightMm * pxPerMm));
    // Search far enough to always land between lines (~2–4 text lines at scale 2)
    const lookbackPx = Math.max(160, Math.floor(usableHeightPx * 0.22));

    let srcY = 0;
    let pageIndex = 0;
    while (srcY < canvas.height - 1) {
      if (pageIndex > 0) pdf.addPage();

      const idealEndY = Math.min(canvas.height, srcY + usableHeightPx);
      const breakY =
        idealEndY >= canvas.height
          ? canvas.height
          : findSafePageBreakY(canvas, srcY, idealEndY, lookbackPx);

      const sliceHeightPx = Math.max(1, breakY - srcY);
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.ceil(sliceHeightPx);
      const ctx = sliceCanvas.getContext("2d");
      if (!ctx) throw new Error("Failed to create PDF page canvas");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        srcY,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );

      const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
      const sliceHeightMm = (sliceCanvas.height * imgWidthMm) / sliceCanvas.width;
      pdf.addImage(sliceData, "JPEG", 0, pagePadMm, imgWidthMm, sliceHeightMm, undefined, "FAST");

      const prevY = srcY;
      srcY = breakY;
      pageIndex += 1;

      // Safety against infinite loops if breakY doesn't advance
      if (srcY <= prevY) {
        srcY = Math.min(canvas.height, prevY + usableHeightPx);
      }
    }

    return pdf.output("blob");
  } finally {
    root.unmount();
    host.remove();
  }
}

export async function downloadAllStudentResumes(
  students: ResumeStudentSource[],
  options?: { zipFileName?: string; onProgress?: (done: number, total: number) => void }
): Promise<void> {
  if (!students.length) {
    throw new Error("No students to download");
  }

  const zip = new JSZip();
  const total = students.length;
  const usedNames = new Set<string>();

  for (let i = 0; i < students.length; i++) {
    const student = students[i];
    const resumeData = mapStudentToResumeData(student);
    const pdfBlob = await resumeDataToPdfBlob(resumeData);

    let fileName = sanitizeResumeFileName(student.student_name, student.student_id);
    if (usedNames.has(fileName)) {
      const base = fileName.replace(/\.pdf$/i, "");
      fileName = `${base}_${i + 1}.pdf`;
    }
    usedNames.add(fileName);
    zip.file(fileName, pdfBlob);
    options?.onProgress?.(i + 1, total);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const zipName = options?.zipFileName || `sent-student-resumes-${Date.now()}.zip`;
  saveAs(zipBlob, zipName);
}
